import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SyncIcon from '@mui/icons-material/Sync';
import React, { FC, useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { URLS } from '../../../../../utils/constants/urls';
import axiosInstance from '../../../../../utils/axios';
import strings from '../../../../../common/Translation/Translate';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import {
  Alert,
  AlertColor,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { Box, useTheme } from '@mui/system';
import Notification from '../../../../../components/Notification';
import General from './General/General';
import { createDropdownData } from '../../../../../utils/validators/HelperFunctions';
import Mapping from './Mapping/Mapping';
import Price from './Price/Price';
import { WeekDays } from '../../../../../utils/constants/data';
import LocalizedAttribute from './LocalizedAttribute/LocalizedAttribute';
import Unavailability from './Unavailability/Unavailability';
import {
  mapUnavailabilityDatesFromApi,
  UnavailabilityDateRange,
} from './Unavailability/unavailabilityDateUtils';
import { routes } from '../../../../../utils/constants/routes';
import Navigation from '../../../../../utils/hooks/Navigation';
import PageLoader from '../../../../../components/PageLoader';
import { privileges } from '../../../../../utils/constants/privileges';
import { Privilege } from '../../../../../utils/redux/reducer/authentication-slice';
import GetQuery from 'utils/hooks/getQuery';
import { pageTitleSx } from 'utils/styles/pageHeader';
import { isHourlyPricingValue } from './General/hourlyPricingFormUtils';
import { VenueConfigurationHealthPanel } from '../VenueConfigurationHealthPanel';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  const theme = useTheme();
  const screenSizeDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}>
      {value === index && (
        <Box sx={{ p: screenSizeDownSm ? 1 : 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}
const AddEditVenueConfiguration: FC = () => {
  const type = GetQuery('type');
  const navigate = useNavigate();
  const { id } = useParams() as any;
  const queryStings = useLocation().search as any;
  const queryStingId = new URLSearchParams(queryStings).get('id') as string;
  const configId = id || queryStingId;
  const privilege = useSelector(Privilege);
  const [pageResponse, setPageResponse] = useState<any>([]);
  const [value, setValue] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [actionLoading, setActionLoading] = useState<'sync' | 'clone' | null>(null);
  const [confirmAction, setConfirmAction] = useState<'sync' | 'clone' | null>(null);
  const [dropdownData, setDropdownData] = useState<any>([]);
  const [unavailabilityDates, setUnavailabilityDates] = useState<UnavailabilityDateRange[]>([]);
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });


  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };
  useEffect(() => {
    getMasterData();
  }, []);
  const getMasterData = async () => {
    const { data } = await axiosInstance.get(URLS.venueConfigDropdownData);
    setDropdownData(data);
  };
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const getPageData = useCallback(
    async (paramId: any) => {
      try {
        setLoading(true);
        const pageId = id || paramId || queryStingId;
        const { data, status } = await axiosInstance.get(URLS.get_edit_delete_VenueConfigurationById(pageId));
        if (status === 200 || status === 201) {
          setLoading(false);
          setPageResponse({ ...data, eventTypeId: type === 'copy' ? '' : data.eventTypeId });
        }
      } catch (error: any) {
        setLoading(false);
      }
    },
    [id, queryStingId],
  );
  useEffect(() => {
    (async () => {
      if (id || queryStingId) {
        await getPageData(null);
      }
    })();
  }, [id, getPageData, queryStingId]);

  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  const tabsDisabled = queryStingId === null && id === undefined;
  const showPriceTab = !isHourlyPricingValue(pageResponse?.hourlyPricing);

  const venueConfigTabs = useMemo(
    () => [
      { key: 'general', label: strings.venueConfig_tabs_general, disabled: false },
      ...(showPriceTab
        ? [{ key: 'price', label: strings.venueConfig_tabs_price, disabled: tabsDisabled }]
        : []),
      { key: 'mapping', label: strings.venueConfig_tabs_mapping, disabled: tabsDisabled },
      { key: 'unavailability', label: strings.venueConfig_tabs_unavailability, disabled: tabsDisabled },
      { key: 'localized', label: strings.venueConfig_tabs_attributes, disabled: tabsDisabled },
    ],
    [showPriceTab, tabsDisabled],
  );

  useEffect(() => {
    if (value >= venueConfigTabs.length) {
      setValue(Math.max(0, venueConfigTabs.length - 1));
    }
  }, [venueConfigTabs.length, value]);

  useEffect(() => {
    if (pageResponse?.unavailabilityDates) {
      setUnavailabilityDates(mapUnavailabilityDatesFromApi(pageResponse.unavailabilityDates));
    }
  }, [pageResponse?.unavailabilityDates]);

  const generalDropdownData = {
    ...dropdownData,
    bookingConfirmationBy: createDropdownData(dropdownData.bookingConfirmationBy, ['id', 'name']),
    serviceCharge: createDropdownData(dropdownData.serviceCharges, ['id', 'name']),
  };
  const mappingDropdownData = {
    ...dropdownData,
    addOns: createDropdownData(dropdownData.addOns, ['id', 'name']),
    layouts: createDropdownData(dropdownData.layouts, ['id', 'name']),
  };
  const priceDrodownData = {
    pricePackageId: createDropdownData(dropdownData.pricePackages, ['id', 'name']),
    pricePackage: createDropdownData(dropdownData.pricePackages, ['id', 'name']),
    weekDays: createDropdownData(WeekDays, ['id', 'name']),
  };

  const canSync = Boolean(configId) && privilege?.includes(privileges.edit_venue_configuration);
  const canClone = Boolean(configId) && privilege?.includes(privileges.add_venue_configuration);

  const handleSyncToSegments = async () => {
    if (!configId || actionLoading) return;
    try {
      setActionLoading('sync');
      const { data, status } = await axiosInstance.post(URLS.venueConfigSyncToSegments(Number(configId)));
      if (status === 200 || status === 201) {
        const count = data?.syncedSegmentCount ?? 0;
        setMessage({
          display: true,
          severity: count > 0 ? 'success' : 'info',
          message:
            count > 0
              ? strings.venueConfig_syncToSegments_success.replace('{count}', String(count))
              : strings.venueConfig_syncToSegments_none,
        });
      }
    } catch (error: any) {
      setMessage({
        display: true,
        severity: 'error',
        message: error.response?.data?.message ?? error.message,
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleClone = async () => {
    if (!configId || actionLoading) return;
    try {
      setActionLoading('clone');
      const { data, status } = await axiosInstance.get(URLS.venueConfigCopy(Number(configId)));
      if (status === 200 && data?.id) {
        navigate(`${routes.editVenueConfiguration}/${data.id}?type=copy`, { replace: true });
      }
    } catch (error: any) {
      setMessage({
        display: true,
        severity: 'error',
        message: error.response?.data?.message ?? error.message,
      });
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <>
      {loading && <PageLoader />}
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      <Dialog
        open={confirmAction === 'sync'}
        onClose={() => !actionLoading && setConfirmAction(null)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberOutlinedIcon color="warning" />
          {strings.venueConfig_sync_confirm_title}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 0 }}>
            {strings.venueConfig_sync_confirm_body}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmAction(null)} disabled={Boolean(actionLoading)}>
            {strings.cancelText}
          </Button>
          <Button
            color="warning"
            variant="contained"
            disabled={Boolean(actionLoading)}
            onClick={() => {
              setConfirmAction(null);
              void handleSyncToSegments();
            }}>
            {strings.venueConfig_sync_confirm_action}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={confirmAction === 'clone'}
        onClose={() => !actionLoading && setConfirmAction(null)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberOutlinedIcon color="info" />
          {strings.venueConfig_clone_confirm_title}
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 0 }}>
            {strings.venueConfig_clone_confirm_body}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmAction(null)} disabled={Boolean(actionLoading)}>
            {strings.cancelText}
          </Button>
          <Button
            color="info"
            variant="contained"
            disabled={Boolean(actionLoading)}
            onClick={() => {
              setConfirmAction(null);
              void handleClone();
            }}>
            {strings.venueConfig_clone_confirm_action}
          </Button>
        </DialogActions>
      </Dialog>
      <Grid container alignItems="center" spacing={1}>
        <Grid item xs>
          <Typography sx={pageTitleSx}>
            {id || queryStingId ? strings.venueConfig_button_update : strings.venueConfig_button_new}
          </Typography>
        </Grid>
        {(canSync || canClone) && (
          <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {canSync && (
              <Button
                variant="outlined"
                color="warning"
                size="small"
                disabled={Boolean(actionLoading)}
                startIcon={
                  actionLoading === 'sync' ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />
                }
                onClick={() => setConfirmAction('sync')}
                sx={{ textTransform: 'none', fontWeight: 600 }}>
                {strings.venueConfig_sync_confirm_action}
              </Button>
            )}
            {canClone && (
              <Button
                variant="outlined"
                color="info"
                size="small"
                disabled={Boolean(actionLoading)}
                startIcon={
                  actionLoading === 'clone' ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <ContentCopyIcon />
                  )
                }
                onClick={() => setConfirmAction('clone')}
                sx={{ textTransform: 'none', fontWeight: 600 }}>
                {strings.venueConfig_clone_confirm_action}
              </Button>
            )}
          </Grid>
        )}
        <Grid item>
          <Navigation url={routes.venueConfiguration} />
        </Grid>
      </Grid>
      {configId && pageResponse?.id && (
        <VenueConfigurationHealthPanel
          value={pageResponse.completionPercentage ?? 0}
          configurationHealth={pageResponse.configurationHealth}
          missingFields={pageResponse.missingFields}
        />
      )}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="basic tabs example"
          variant="scrollable"
          allowScrollButtonsMobile
          scrollButtons="auto"
          sx={{
            '.MuiTabScrollButton-root.Mui-disabled': {
              display: 'none',
            },
          }}>
          {venueConfigTabs.map((tab, index) => (
            <Tab key={tab.key} disabled={tab.disabled} label={tab.label} {...a11yProps(index)} />
          ))}
        </Tabs>
      </Box>
      {venueConfigTabs.map((tab, index) => (
        <TabPanel key={tab.key} value={value} index={index}>
          {tab.key === 'general' && (
            <General
              getPageData={getPageData}
              pageResponse={pageResponse}
              dropdownData={generalDropdownData}
              setPageResponse={setPageResponse}
              unavailabilityDates={unavailabilityDates}
              setUnavailabilityDates={setUnavailabilityDates}
            />
          )}
          {tab.key === 'price' && (
            <Price getPageData={getPageData} pageResponse={pageResponse} dropdownData={priceDrodownData} />
          )}
          {tab.key === 'mapping' && (
            <Mapping getPageData={getPageData} pageResponse={pageResponse} dropdownData={mappingDropdownData} />
          )}
          {tab.key === 'unavailability' && (
            <Unavailability
              getPageData={getPageData}
              pageResponse={pageResponse}
              dates={unavailabilityDates}
              setDates={setUnavailabilityDates}
            />
          )}
          {tab.key === 'localized' && (
            <LocalizedAttribute pageResponse={pageResponse} getPageData={getPageData} />
          )}
        </TabPanel>
      ))}
    </>
  );
};
export default AddEditVenueConfiguration;
